/**
 * Single named representation of `ProjectList`'s loading-related states.
 *
 * `isPending` is structurally exclusive with the other two (no data can't also be
 * paginating or showing placeholder rows). `isPlaceholderData` and `isFetchingNextPage`
 * can overlap, though: the "Load more" button stays clickable during a query-change
 * refetch, so a click can start pagination while placeholder rows are still showing.
 * The deriver's precedence resolves that overlap to `loadingMore`.
 *
 * - `loading`     – no data yet (first paint or cross-route remount)
 * - `refetching`  – query change in flight; previous rows kept visible via `keepPreviousData`
 * - `loadingMore` – bottom-of-list "Load more" pagination in flight
 * - `ready`       – data settled, no fetch in flight
 */
export type ProjectListLoadingState = 'ready' | 'loading' | 'refetching' | 'loadingMore'

interface ProjectListLoadingFlags {
  isPending: boolean
  isPlaceholderData: boolean
  isFetchingNextPage: boolean
}

export function getProjectListLoadingState({
  isPending,
  isPlaceholderData,
  isFetchingNextPage,
}: ProjectListLoadingFlags): ProjectListLoadingState {
  if (isPending) return 'loading'
  // Checked before `isPlaceholderData` so the one possible overlap resolves to `loadingMore`.
  if (isFetchingNextPage) return 'loadingMore'
  if (isPlaceholderData) return 'refetching'
  return 'ready'
}
